import { utils, write } from 'xlsx';
import { MimeType } from '@/utils/fileValidation';
import type {
  AccountsExportScalar,
  AccountsExportTable,
} from './accountsExport';

type NormalizedAccountsExportScalar = string | number | boolean;

const spreadsheetFormulaPrefixes: readonly string[] = [
  '=',
  '+',
  '-',
  '@',
  '\t',
  '\r',
];

function normalizeExportScalar(
  value: AccountsExportScalar,
): NormalizedAccountsExportScalar {
  return value === null ? '' : value;
}

function neutralizeFormulaScalar(
  value: AccountsExportScalar,
): NormalizedAccountsExportScalar {
  if (
    typeof value === 'string' &&
    spreadsheetFormulaPrefixes.includes(value.charAt(0))
  ) {
    return `'${value}`;
  }

  return normalizeExportScalar(value);
}

function worksheetRows(
  table: AccountsExportTable,
): NormalizedAccountsExportScalar[][] {
  return [
    table.columns.map((column) => neutralizeFormulaScalar(column.headerName)),
    ...table.rows.map((row) => row.map(neutralizeFormulaScalar)),
  ];
}

function exportScalarText(value: AccountsExportScalar): string {
  const normalized = normalizeExportScalar(value);
  if (typeof normalized === 'string') {
    return normalized;
  }
  if (typeof normalized === 'number') {
    return normalized.toString();
  }
  return normalized ? 'true' : 'false';
}

function encodeCsvCell(value: AccountsExportScalar): string {
  const text = exportScalarText(value);
  const safeText =
    typeof value === 'string' &&
    spreadsheetFormulaPrefixes.includes(text.charAt(0))
      ? `'${text}`
      : text;
  const escapedText = safeText.replace(/"/g, '""');
  return `"${escapedText}"`;
}

export function encodeAccountsExportCsv(table: AccountsExportTable): Blob {
  const rows: AccountsExportScalar[][] = [
    table.columns.map((column) => column.headerName),
    ...table.rows,
  ];
  const csv = rows.map((row) => row.map(encodeCsvCell).join(',')).join('\n');

  return new Blob([csv], { type: MimeType.Csv });
}

export function encodeAccountsExportXlsx(table: AccountsExportTable): Blob {
  const worksheet = utils.aoa_to_sheet(worksheetRows(table));
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Accounts');
  const buffer = write(workbook, {
    type: 'array',
    bookType: 'xlsx',
  });

  return new Blob([buffer], { type: MimeType.Xlsx });
}
